import { useQuery } from "react-query";
import { planApi } from "@/react/api";
import { usePriceTestingEnabled } from "@/react/hooks/experiments/usePriceTestingEnabled";
import type { Plan } from "@circle-react/types";

interface UseFetchPlansProps {
  billing_currency: string | null;
  billing_cycle: string | null;
  locale: string | null;
}

interface UseQueryFetchPlans {
  isRefetchingPlans: boolean;
  plans: Plan[];
}

const PLANS_CACHE_TIME = 1000 * 60 * 10; // 10 minutes
const PLANS_STALE_TIME = 1000 * 60; // 1 minute

interface UseFetchPlansResult extends UseQueryFetchPlans {
  errorFetchingPlans: any; // [TODO][BillingReactification][AfterRelease] Create error type
  isPlansLoading: boolean;
  isRefetchingPlans: boolean;
  refetchPlans: any;
}
export const useFetchPlans = ({
  billing_cycle,
  billing_currency,
  locale,
}: UseFetchPlansProps): UseFetchPlansResult => {
  const isPriceTestingEnabled = usePriceTestingEnabled();

  const {
    data,
    isLoading: isPlansLoading,
    error: errorFetchingPlans,
    refetch: refetchPlans,
    isRefetching: isRefetchingPlans,
  } = useQuery<UseQueryFetchPlans, Error>(
    ["plans", billing_cycle, billing_currency, locale, isPriceTestingEnabled],
    () =>
      planApi.fetchPlans({
        billing_cycle,
        billing_currency,
        locale,
        isPriceTestingEnabled,
      }),
    {
      enabled: !!billing_cycle,
      cacheTime: PLANS_CACHE_TIME,
      staleTime: PLANS_STALE_TIME,
    },
  );

  const auxiliaryBillingCycle = billing_cycle === "month" ? "year" : "month";
  useQuery<UseQueryFetchPlans, Error>(
    [
      "plans",
      auxiliaryBillingCycle,
      billing_currency,
      locale,
      isPriceTestingEnabled,
    ],
    () =>
      planApi.fetchPlans({
        billing_cycle: auxiliaryBillingCycle,
        billing_currency,
        locale,
        isPriceTestingEnabled,
      }),
    {
      enabled: !!billing_cycle,
      cacheTime: PLANS_CACHE_TIME,
      staleTime: PLANS_STALE_TIME,
    },
  );

  const plans = data?.plans ?? [];

  return {
    plans,
    isPlansLoading,
    errorFetchingPlans,
    refetchPlans,
    isRefetchingPlans,
  };
};
